/**
 * The billing-cycle forecast (KAN-52).
 *
 * By default everything here comes from one stored snapshot, and the header says
 * when it was generated. That matters more than it sounds: the acceptance
 * criterion is that the forecast exists *before* the cycle closes, which is a
 * fact about the data and not about when somebody opened this page.
 *
 * The projection is stated in full rather than presented as a single number,
 * because the parts are what get argued about — how much is held in trust
 * already, how much becomes a receivable, and what history says the firm
 * actually collects on receivables.
 *
 * Choosing cycle dates asks a different question, and the page changes what it
 * says about itself when it does. The snapshot is the cycle about to be billed;
 * a chosen window is projected live and bounded at both ends, so its figures
 * are not the snapshot's and are never presented as though they were.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BILL_STATUSES,
  BILL_STATUS_META,
  formatBps,
  type BillStatus,
  type BillingForecastTrigger,
} from '@accounting/shared'
import { ApiError, apiDownload, apiFetch } from '../lib/api'
import { formatDateTime, formatHours, formatMoney, formatRelative } from '../lib/format'
import { useSession } from '../lib/session'
import { Alert } from '../ui/Alert'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { Field, Select, TextInput } from '../ui/Field'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'
import { SyncIcon } from '../ui/icons'

interface Forecast {
  /**
   * Null on a projection over chosen dates: nothing was stored, so there is no
   * row to address and nothing triggered it. The two nulls travel together and
   * are what tell a projection from a snapshot.
   */
  id: string | null
  /** Null when the window is open at the near end — everything still unbilled. */
  cycleStart: string | null
  cycleEnd: string
  generatedAt: string
  trigger: BillingForecastTrigger | null
  realizationRateBps: number
  realizationSampleInvoices: number
  realizationIsAssumed: boolean
  wipMinor: number
  blockedMinor: number
  blockingExceptionCount: number
  trustAvailableMinor: number
  unallocatedTrustMinor: number
  projectedTrustTransferMinor: number
  projectedNewArMinor: number
  projectedCashMinor: number
  matterCount: number
}

interface ForecastLine {
  /** Null on a projected line. Drilled into by matter and window instead. */
  id: string | null
  matterId: string
  matterDisplayNumber: string | null
  matterDescription: string | null
  clientName: string | null
  attorneyName: string | null
  unbilledSeconds: number
  entryCount: number
  wipMinor: number
  trustAvailableMinor: number
  projectedTrustTransferMinor: number
  projectedNewArMinor: number
  projectedCashMinor: number
  /**
   * What the matter still owes on invoices already issued, read live whichever
   * dates are shown. Zero when nothing is outstanding.
   */
  arOutstandingMinor: number
}

interface LatestForecast {
  forecast: Forecast | null
  lines: ForecastLine[]
}

interface LineEntry {
  id: string
  workedAt: string
  description: string | null
  durationSeconds: number
  rateMinor: number | null
  valueMinor: number | null
  attorneyName: string | null
  billStatus: BillStatus
  invoiceNumber: string | null
  sourceSystem: string
  sourceId: string | null
}

interface NoticeRecipient {
  id: string
  name: string
  email: string
  role: string
}

interface NoticeResult {
  sent: string[]
  notPermitted: string[]
  unknown: string[]
}

const TRIGGER_LABELS: Record<BillingForecastTrigger, string> = {
  sync: 'after a sync',
  manual: 'on request',
  scheduled: 'on schedule',
}

/** How many closed months to offer as one-click cycles. */
const RECENT_CYCLES = 6

/** Work dates as `YYYY-MM-DD` — the format the date input speaks. */
interface CycleDates {
  from: string
  to: string
}

const NO_CYCLE: CycleDates = { from: '', to: '' }

/**
 * The last few whole months, newest first.
 *
 * A cycle is a calendar month here, so picking one should be one click rather
 * than two dates typed correctly. The custom range stays available underneath
 * for the quarter-end and mid-month questions the preset cannot express.
 */
function recentCycles(): { value: string; label: string; dates: CycleDates }[] {
  const cycles = []
  const now = new Date()

  for (let back = 0; back < RECENT_CYCLES; back += 1) {
    const start = new Date(now.getFullYear(), now.getMonth() - back, 1)
    /** Day zero of the next month is the last day of this one, leap years included. */
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0)

    cycles.push({
      value: iso(start),
      label: start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      dates: { from: iso(start), to: iso(end) },
    })
  }

  return cycles
}

/** Today alone, as both ends of the window. */
function today(): CycleDates {
  const now = iso(new Date())
  return { from: now, to: now }
}

/** Local calendar date as `YYYY-MM-DD`, never via `toISOString` — that is UTC. */
function iso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

type ExportFormat = 'xlsx' | 'pdf'

const EXPORT_TYPES: Record<ExportFormat, string> = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
}

export function BillingForecast() {
  const { may } = useSession()
  const queryClient = useQueryClient()
  const [expanded, setExpanded] = useState<string | null>(null)
  /**
   * Opens on today, so the first thing shown is today's work projected live.
   * The stored snapshot is one click away, under "Back to the cycle being
   * billed".
   */
  const [cycle, setCycle] = useState<CycleDates>(today)
  /**
   * Empty means the whole population the next bill is raised from — unbilled
   * plus draft — which is exactly what the stored snapshot covers. Choosing one
   * narrows the question and, like choosing dates, switches the page to a live
   * projection: the snapshot is a single set of figures and cannot be sliced
   * after the fact without recomputing it.
   */
  const [billStatus, setBillStatus] = useState<BillStatus | ''>('')

  const cycles = useMemo(recentCycles, [])
  const filtered = Boolean(cycle.from || cycle.to || billStatus)

  const query = new URLSearchParams()
  if (cycle.from) query.set('from', cycle.from)
  if (cycle.to) query.set('to', cycle.to)
  if (billStatus) query.set('billStatus', billStatus)

  /**
   * Two queries, only one of them enabled. The snapshot and the projection are
   * different resources — one stored, one computed — and keeping them apart
   * means switching back to the snapshot shows it immediately from cache
   * rather than re-fetching what has not changed.
   */
  const latest = useQuery({
    queryKey: ['billing-forecast'],
    queryFn: () => apiFetch<LatestForecast>('/billing-forecast'),
    enabled: !filtered,
  })

  const preview = useQuery({
    queryKey: ['billing-forecast-preview', cycle.from, cycle.to, billStatus],
    queryFn: () => apiFetch<LatestForecast>(`/billing-forecast/preview?${query.toString()}`),
    enabled: filtered,
  })

  const active = filtered ? preview : latest

  const generate = useMutation({
    mutationFn: () =>
      apiFetch<{ forecast: Forecast }>('/billing-forecast/generate', { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['billing-forecast'] })
      void queryClient.invalidateQueries({ queryKey: ['billing-forecast-preview'] })
    },
  })

  /**
   * The figures on screen as a workbook or a PDF. Same query string as the
   * screen, so the API picks the stored run or the live projection exactly as
   * it does for the page.
   */
  const download = useMutation({
    mutationFn: (format: ExportFormat) => {
      const params = new URLSearchParams(query)
      params.set('format', format)
      return apiDownload(
        `/billing-forecast/export?${params.toString()}`,
        `billing-forecast-${iso(new Date())}.${format}`,
        EXPORT_TYPES[format]
      )
    },
  })

  /** Collapse the drill-down: the row it was opened on may not be in the new window. */
  function changeCycle(next: CycleDates) {
    setCycle(next)
    setExpanded(null)
  }

  function changeBillStatus(next: BillStatus | '') {
    setBillStatus(next)
    setExpanded(null)
  }

  const forecast = active.data?.forecast ?? null
  const lines = active.data?.lines ?? []

  return (
    <>
      <PageHeader
        title="Billing forecast"
        description="What the coming billing run looks like from current work in progress, the trust already held against it, and what history says the firm collects."
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => download.mutate('xlsx')}
              disabled={download.isPending || !forecast}
            >
              {download.isPending && download.variables === 'xlsx' ? 'Preparing…' : 'Export to Excel'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => download.mutate('pdf')}
              disabled={download.isPending || !forecast}
            >
              {download.isPending && download.variables === 'pdf' ? 'Preparing…' : 'Export to PDF'}
            </Button>
            {may('billing_forecast.generate') && (
              <Button
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                title={
                  filtered
                    ? 'Regenerates the stored forecast for the cycle being billed, not the dates shown'
                    : undefined
                }
              >
                <SyncIcon />
                {generate.isPending ? 'Generating…' : 'Regenerate'}
              </Button>
            )}
          </div>
        }
      />

      {download.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(download.error)}
        </Alert>
      )}

      {generate.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(generate.error)}
        </Alert>
      )}

      {active.error && (
        <Alert tone="error" className="mb-4">
          {messageFor(active.error)}
        </Alert>
      )}

      <Card className="mb-6">
        <CardBody>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <Field
              id="cycle-preset"
              label="Cycle"
              hint="A closed month, or set the dates yourself below."
            >
              <Select
                id="cycle-preset"
                value={cycles.find((option) => matches(option.dates, cycle))?.value ?? ''}
                onChange={(event) => {
                  const chosen = cycles.find((option) => option.value === event.target.value)
                  changeCycle(chosen ? chosen.dates : NO_CYCLE)
                }}
              >
                <option value="">The cycle being billed</option>
                {cycles.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            {/*
              * Work dates, matching the exceptions queue: the question is
              * "what was done in these dates", and both ends are inclusive.
              * Each bounds the other so a range cannot be entered backwards.
              */}
            <Field id="cycle-from" label="Worked from" hint="Both dates are included.">
              <TextInput
                id="cycle-from"
                type="date"
                value={cycle.from}
                max={cycle.to || undefined}
                onChange={(event) => changeCycle({ ...cycle, from: event.target.value })}
              />
            </Field>

            <Field
              id="cycle-to"
              label="Worked to"
              hint="Leave either blank for open-ended."
            >
              <TextInput
                id="cycle-to"
                type="date"
                value={cycle.to}
                min={cycle.from || undefined}
                onChange={(event) => changeCycle({ ...cycle, to: event.target.value })}
              />
            </Field>

            {/*
              * Which side of the coming bill to look at. The default covers
              * both, because both are billed together — this splits "already
              * gathered onto a draft" from "not yet", which are different
              * conversations with the billing specialist.
              */}
            <Field
              id="bill-status"
              label="Invoice status"
              hint="The forecast covers unbilled and draft together unless you narrow it."
            >
              <Select
                id="bill-status"
                value={billStatus}
                onChange={(event) =>
                  changeBillStatus(event.target.value as BillStatus | '')
                }
              >
                <option value="">Unbilled and draft</option>
                {BILL_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {BILL_STATUS_META[option].label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {billStatus === 'billed' && (
            <Alert tone="info" className="mt-4" title="This is not a forecast">
              Work already on an issued bill has been billed — what you are looking at is its
              value, not a projection of anything. Trust and realization are still applied, so
              the cash column reads as "what this work is expected to collect", not as new
              money.
            </Alert>
          )}

          {filtered && (
            <div className="mt-4">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  changeCycle(NO_CYCLE)
                  changeBillStatus('')
                }}
              >
                Back to the cycle being billed
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/*
        * Said before the figures, not after. A window bounded at the near end
        * leaves out older work that is still unbilled and *will* be on the next
        * invoice, so these numbers are smaller than the ones the firm is about
        * to bill — which is only misleading if nobody says so first.
        */}
      {filtered && (
        <Alert
          tone="info"
          className="mb-4"
          title={
            cycle.from || cycle.to
              ? 'Projected now, for the dates chosen'
              : 'Projected now, for the status chosen'
          }
        >
          {(cycle.from || cycle.to) &&
            'Only work done in this window counts, so unbilled time from before it is left out. '}
          Nothing is stored — the snapshot for the cycle being billed is untouched.
        </Alert>
      )}

      {active.isPending && <p className="text-sm text-ink-600">Loading the forecast…</p>}

      {!active.isPending && !forecast && !filtered && (
        <Alert tone="info" title="No forecast has been generated yet">
          The forecast is regenerated automatically whenever time entries or trust movements sync.
          Until Clio is connected there is nothing to project from —{' '}
          <Link to="/integrations" className="underline">
            integration status
          </Link>{' '}
          shows what is waiting on credentials.
        </Alert>
      )}

      {forecast && (
        <>
          <p className="mb-4 text-sm text-ink-600">
            {forecast.cycleStart
              ? `Cycle ${formatDate(forecast.cycleStart)} – ${formatDate(forecast.cycleEnd)}. `
              : `Everything unbilled up to ${formatDate(forecast.cycleEnd)}. `}
            {forecast.trigger
              ? `Generated ${formatRelative(forecast.generatedAt)} ${TRIGGER_LABELS[forecast.trigger]}, covering `
              : 'Projected just now, covering '}
            {forecast.matterCount} {forecast.matterCount === 1 ? 'matter' : 'matters'}.
          </p>

          <StatRow className="mb-3">
            <Stat
              label="Work in progress"
              value={formatMoney(forecast.wipMinor)}
              hint="Unbilled billable time on a matter, at the rate recorded."
            />
            <Stat
              label="From trust"
              value={formatMoney(forecast.projectedTrustTransferMinor)}
              hint="Capped at the trust held for each matter. Money already in hand."
            />
            <Stat
              label="New receivables"
              value={formatMoney(forecast.projectedNewArMinor)}
              hint="The part of the billing that has to be collected."
            />
            <Stat
              label="Projected cash"
              value={formatMoney(forecast.projectedCashMinor)}
              hint={
                forecast.realizationIsAssumed
                  ? 'Trust in full plus receivables at an assumed 100% — no settled invoice history to measure yet.'
                  : `Trust in full plus receivables at ${formatBps(
                      forecast.realizationRateBps
                    )}, from ${forecast.realizationSampleInvoices} settled ${
                      forecast.realizationSampleInvoices === 1 ? 'invoice' : 'invoices'
                    }.`
              }
            />
          </StatRow>

          <StatRow className="mb-6">
            <Stat
              label="Blocked"
              value={formatMoney(forecast.blockedMinor)}
              tone={forecast.blockedMinor > 0 ? 'warning' : 'muted'}
              hint={
                forecast.blockingExceptionCount > 0 ? (
                  <>
                    Billable time with no matter, held out of every figure above.{' '}
                    <Link to="/billing-exceptions" className="text-brand-700 underline">
                      {forecast.blockingExceptionCount} to assign
                    </Link>
                  </>
                ) : (
                  'No billable time is missing a matter.'
                )
              }
            />
            <Stat
              label="Trust held"
              value={formatMoney(forecast.trustAvailableMinor)}
              tone="muted"
              hint="Against the matters in this forecast."
            />
            <Stat
              label="Unallocated trust"
              value={formatMoney(forecast.unallocatedTrustMinor)}
              tone="muted"
              hint="Held for a client but not against a matter. Reported, never projected — which bill it pays is Finance's call."
            />
            <Stat
              label="Realization applied"
              value={formatBps(forecast.realizationRateBps)}
              tone={forecast.realizationIsAssumed ? 'muted' : 'default'}
              hint={
                forecast.realizationIsAssumed
                  ? 'Assumed, from a sample of none.'
                  : 'Collected against billed, on invoices whose outcome is settled.'
              }
            />
          </StatRow>

          {/*
            * Only on the snapshot. The email quotes the stored forecast for the
            * cycle being billed — which is the thing a billing specialist acts
            * on — so offering the button beside a what-if projection would
            * promise to send figures it is not going to send.
            */}
          {!filtered && may('billing_forecast.generate') && <ForecastNotice />}

          <Card>
            <CardHeader
              title="By matter"
              description="Every matter with unbilled billable time in this cycle. Expand one to see the entries behind it."
              actions={<Badge tone="neutral">{lines.length}</Badge>}
            />

            <TableWrap>
              <thead>
                <tr>
                  <Th>Matter</Th>
                  <Th className="text-right">Unbilled</Th>
                  <Th className="text-right">WIP</Th>
                  <Th className="text-right">From trust</Th>
                  <Th className="text-right">New AR</Th>
                  <Th className="text-right">Projected cash</Th>
                  <Th className="text-right">
                    <span title="Still owed on invoices already issued, as of now — not limited to the dates shown.">
                      Current AR
                    </span>
                  </Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <EmptyRow colSpan={8}>
                    No matter has unbilled billable time in this cycle.
                  </EmptyRow>
                )}

                {/*
                  * Keyed by matter, not by line id: a projected line has no id,
                  * and the matter is what the row is about in either case.
                  */}
                {lines.map((line) => (
                  <LineRow
                    key={line.matterId}
                    line={line}
                    window={filtered ? cycle : null}
                    billStatus={billStatus}
                    expanded={expanded === line.matterId}
                    onToggle={() =>
                      setExpanded(expanded === line.matterId ? null : line.matterId)
                    }
                  />
                ))}
              </tbody>
            </TableWrap>
          </Card>

          <p className="mt-6 max-w-3xl text-xs leading-relaxed text-ink-600">
            Trust transfers are projected at face value because that money is already held; only the
            receivable is discounted by realization. Trust and realization are both read as they
            stand today, whichever dates are shown.{' '}
            {forecast.trigger ? (
              <>
                Figures are the snapshot's, taken {formatDateTime(forecast.generatedAt)} — drilling
                into a matter reads its entries as they are now, so the two can differ if something
                changed since.
              </>
            ) : (
              <>
                These figures were computed just now and nothing was stored, so they agree with the
                entries behind them.
              </>
            )}
          </p>
        </>
      )}
    </>
  )
}

/**
 * Emailing the coming cycle's receivable to whoever chases it.
 *
 * The firm's ask: "ability to send out email to notify billing specialist of
 * potential AR for upcoming cycle." A person presses this when the forecast is
 * settled enough to act on, which is why it is a button and not a threshold
 * rule — see the service for the longer version of that argument.
 *
 * Recipients come from the server, already filtered to accounts that may see
 * firm financials. The same check runs again on send: this list exists so there
 * is something to tick, not so it can be trusted.
 */
function ForecastNotice() {
  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<string[]>([])

  const recipients = useQuery({
    queryKey: ['billing-forecast-notice-recipients'],
    queryFn: () =>
      apiFetch<{ recipients: NoticeRecipient[] }>('/billing-forecast/notice-recipients'),
    enabled: open,
  })

  const notify = useMutation({
    mutationFn: () =>
      apiFetch<NoticeResult>('/billing-forecast/notify', {
        method: 'POST',
        body: JSON.stringify({ recipientUserIds: chosen }),
      }),
  })

  const people = recipients.data?.recipients ?? []

  function toggle(id: string) {
    setChosen((current) =>
      current.includes(id) ? current.filter((other) => other !== id) : [...current, id]
    )
    /** A new selection has not been sent, so the last result no longer describes it. */
    notify.reset()
  }

  return (
    <Card className="mb-6">
      <CardHeader
        title="Tell the billing specialist"
        description="Emails the figures above — projected receivable, what comes from trust, and the largest matters behind it."
        actions={
          <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} aria-expanded={open}>
            {open ? 'Close' : 'Send by email'}
          </Button>
        }
      />

      {open && (
        <CardBody>
          {recipients.isPending && <p className="text-sm text-ink-600">Loading recipients…</p>}
          {recipients.error && <Alert tone="error">{messageFor(recipients.error)}</Alert>}

          {!recipients.isPending && !recipients.error && people.length === 0 && (
            <Alert tone="info">
              No active account holds the grant needed to be sent firm financials, so there is
              nobody to send this to.
            </Alert>
          )}

          {people.length > 0 && (
            <>
              <div className="mb-4 space-y-2">
                {people.map((person) => (
                  <label key={person.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={chosen.includes(person.id)}
                      onChange={() => toggle(person.id)}
                      className="rounded border-ink-300"
                    />
                    <span className="text-ink-900">{person.name}</span>
                    <span className="text-xs text-ink-600">{person.email}</span>
                  </label>
                ))}
              </div>

              {notify.error && (
                <Alert tone="error" className="mb-3">
                  {notify.error instanceof ApiError && notify.error.code === 'mail_not_configured'
                    ? 'Outbound email is not configured on this deployment, so nothing was sent.'
                    : notify.error instanceof ApiError && notify.error.code === 'no_forecast'
                      ? 'There is no forecast to send yet. Regenerate it first.'
                      : messageFor(notify.error)}
                </Alert>
              )}

              {/*
                * Partial outcomes reported as partial. "Sent to two of the three
                * you picked" is the fact, and collapsing it to success would
                * leave somebody believing a person was told who was not.
                */}
              {notify.data && (
                <Alert
                  tone={notify.data.sent.length > 0 ? 'success' : 'warning'}
                  className="mb-3"
                >
                  {notify.data.sent.length > 0
                    ? `Sent to ${notify.data.sent.join(', ')}.`
                    : 'Nothing was sent.'}
                  {notify.data.notPermitted.length > 0 &&
                    ` ${notify.data.notPermitted.join(', ')} may not be sent firm financials, so ${
                      notify.data.notPermitted.length === 1 ? 'that one was' : 'those were'
                    } skipped.`}
                </Alert>
              )}

              <Button
                variant="accent"
                size="sm"
                disabled={chosen.length === 0 || notify.isPending}
                onClick={() => notify.mutate()}
              >
                {notify.isPending ? 'Sending…' : `Send to ${chosen.length || 'nobody'}`}
              </Button>

              <p className="mt-3 max-w-2xl text-xs text-ink-600">
                The subject line carries no figure — it renders on a locked phone. The amounts
                are in the body.
              </p>
            </>
          )}
        </CardBody>
      )}
    </Card>
  )
}

function LineRow({
  line,
  window,
  billStatus,
  expanded,
  onToggle,
}: {
  line: ForecastLine
  /** The chosen cycle dates, or null when this row came from the snapshot. */
  window: CycleDates | null
  billStatus: BillStatus | ''
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <>
      <tr className={expanded ? 'bg-brand-50/50' : undefined}>
        <Td>
          {/* The description is often a paragraph, so it is the cell's title rather than its text. */}
          <span className="block text-ink-900" title={line.matterDescription ?? undefined}>
            {line.matterDisplayNumber ?? '—'}
          </span>
          <span className="block text-xs text-ink-600">
            {line.clientName ?? 'Client not synced'}
            {line.attorneyName ? ` · ${line.attorneyName}` : ''}
          </span>
        </Td>
        <Td className="text-right tabular-nums">
          <span className="block">{formatHours(line.unbilledSeconds)}</span>
          <span className="block text-xs text-ink-600">
            {line.entryCount} {line.entryCount === 1 ? 'entry' : 'entries'}
          </span>
        </Td>
        <Td className="text-right tabular-nums">{formatMoney(line.wipMinor)}</Td>
        <Td className="text-right tabular-nums">
          {formatMoney(line.projectedTrustTransferMinor)}
        </Td>
        <Td className="text-right tabular-nums">{formatMoney(line.projectedNewArMinor)}</Td>
        <Td className="text-right tabular-nums">{formatMoney(line.projectedCashMinor)}</Td>
        <Td className="text-right tabular-nums">
          {line.arOutstandingMinor > 0 ? (
            <span className="text-accent-700">{formatMoney(line.arOutstandingMinor)}</span>
          ) : (
            <span className="text-ink-500">None</span>
          )}
        </Td>
        <Td className="text-right">
          <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Entries'}
          </Button>
        </Td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} className="border-b border-ink-200 bg-ink-50 px-5 py-4">
            <LineEntries line={line} window={window} billStatus={billStatus} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * The drill-down: the unbilled entries this line was built from, each with the
 * upstream id needed to find it in Clio. Loaded on expand rather than with the
 * page — a forecast covering a hundred matters would otherwise fetch every
 * entry in the firm to show six.
 */
function LineEntries({
  line,
  window,
  billStatus,
}: {
  line: ForecastLine
  window: CycleDates | null
  billStatus: BillStatus | ''
}) {
  /**
   * A stored line is addressed by its id; a projected one has none, so it is
   * addressed by matter and the same window that produced it. Either way the
   * entries listed are the ones the figures beside them were built from.
   */
  const path = line.id
    ? `/billing-forecast/lines/${line.id}/entries`
    : `/billing-forecast/matters/${line.matterId}/entries?${entryQuery(window, billStatus)}`

  const entries = useQuery({
    queryKey: ['billing-forecast-entries', path],
    queryFn: () => apiFetch<{ entries: LineEntry[] }>(path),
  })

  if (entries.isPending) return <p className="text-sm text-ink-600">Loading entries…</p>
  if (entries.error) return <Alert tone="error">{messageFor(entries.error)}</Alert>

  const rows = entries.data?.entries ?? []

  return (
    <div className="overflow-x-auto rounded-md border border-ink-200 bg-white">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <Th>Worked</Th>
            <Th>Description</Th>
            <Th>Attorney</Th>
            <Th className="text-right">Hours</Th>
            <Th className="text-right">Rate</Th>
            <Th className="text-right">Value</Th>
            <Th>Invoice</Th>
            <Th>Source</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <EmptyRow colSpan={8}>
              Nothing unbilled on this matter any more — it has been billed or reassigned since the
              forecast was generated.
            </EmptyRow>
          )}
          {rows.map((entry) => (
            <tr key={entry.id}>
              <Td className="whitespace-nowrap">{formatDateTime(entry.workedAt)}</Td>
              <Td>{entry.description ?? <span className="text-accent-700">No description</span>}</Td>
              <Td>{entry.attorneyName ?? '—'}</Td>
              <Td className="text-right tabular-nums">{formatHours(entry.durationSeconds)}</Td>
              <Td className="text-right tabular-nums">{formatMoney(entry.rateMinor)}</Td>
              <Td className="text-right tabular-nums">{formatMoney(entry.valueMinor)}</Td>
              <Td className="whitespace-nowrap">
                {entry.billStatus === 'unbilled' ? (
                  <span className="text-ink-500">Not on a bill</span>
                ) : (
                  <>
                    {BILL_STATUS_META[entry.billStatus].label}
                    {entry.invoiceNumber ? ` ${entry.invoiceNumber}` : ''}
                  </>
                )}
              </Td>
              <Td>
                <code className="font-mono text-[11px] text-ink-500">
                  {entry.sourceSystem}
                  {entry.sourceId ? ` ${entry.sourceId}` : ''}
                </code>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function entryQuery(window: CycleDates | null, billStatus: BillStatus | ''): string {
  const query = new URLSearchParams()
  if (window?.from) query.set('from', window.from)
  if (window?.to) query.set('to', window.to)
  /** Same narrowing as the figures above, or the entries would not add up. */
  if (billStatus) query.set('billStatus', billStatus)
  return query.toString()
}

/** Whether a preset's dates are the ones currently in the inputs. */
function matches(preset: CycleDates, chosen: CycleDates): boolean {
  return preset.from === chosen.from && preset.to === chosen.to
}

/** Cycle bounds are dates, not moments — a time of day would imply precision. */
function formatDate(value: string): string {
  const parsed = new Date(value)
  return Number.isNaN(parsed.valueOf())
    ? value
    : parsed.toLocaleDateString(undefined, { dateStyle: 'medium' })
}

function messageFor(error: unknown): string {
  return error instanceof ApiError
    ? `${error.message} (${error.status})`
    : 'Something went wrong. Try again.'
}
